/**
 * Shipping countdown & delivery estimate for TMCF USA.
 * All date/time logic uses US Pacific Time (America/Los_Angeles) regardless of
 * the visitor's local timezone.
 *
 * Phase rules (Pacific Time):
 *   Phase A  Fri 01:00 → Mon 01:00  │ cutoff Mon 01:00  │ ships Tue  │ SoCal Wed  │ NorCal Thu
 *   Phase B  Mon 01:00 → Tue 01:00  │ cutoff Tue 01:00  │ ships Wed  │ SoCal Thu  │ NorCal Fri
 *   Phase C  Tue 01:00 → Fri 01:00  │ cutoff Fri 01:00  │ ships Mon  │ SoCal Tue  │ NorCal Wed
 */

(function () {
  'use strict';

  const TZ = 'America/Los_Angeles';

  // cutoffDow: day-of-week index (0=Sun … 6=Sat) when this phase's cutoff falls
  // cutoffHour: hour in Pacific time of the cutoff (1 = 01:00 AM)
  // shipOffset: days from the cutoff *calendar day* to the shipping day
  // socalOffset / norcalOffset: days from ship day to delivery
  const PHASES = [
    { id: 'A', cutoffDow: 1, cutoffHour: 1, shipOffset: 1, socalOffset: 1, norcalOffset: 2 },
    { id: 'B', cutoffDow: 2, cutoffHour: 1, shipOffset: 1, socalOffset: 1, norcalOffset: 2 },
    { id: 'C', cutoffDow: 5, cutoffHour: 1, shipOffset: 3, socalOffset: 1, norcalOffset: 2 },
  ];

  const DOW = {
    Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
    Thursday: 4, Friday: 5, Saturday: 6,
  };

  /** Return Pacific-time date parts for a given UTC Date. */
  function getPT(date) {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: TZ,
      weekday: 'long', year: 'numeric', month: 'numeric',
      day: 'numeric', hour: 'numeric', minute: 'numeric',
      second: 'numeric', hour12: false,
    });
    const p = Object.fromEntries(fmt.formatToParts(date).map(x => [x.type, x.value]));
    return {
      dow: DOW[p.weekday],
      h: parseInt(p.hour) % 24, // guard against browsers returning "24" for midnight
      m: parseInt(p.minute),
      day: parseInt(p.day),
      month: parseInt(p.month),
      year: parseInt(p.year),
    };
  }

  const pad = n => String(n).padStart(2, '0');

  /**
   * Convert a Pacific calendar date + time to a UTC Date.
   * Tries PDT (UTC-7) then PST (UTC-8) and returns whichever actually
   * resolves to the requested Pacific local time.
   */
  function ptToUTC(year, month, day, hour, min = 0) {
    for (const off of ['-07:00', '-08:00']) {
      const iso = `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(min)}:00${off}`;
      const d = new Date(iso);
      const c = getPT(d);
      if (c.h === hour && c.day === day && c.month === month && c.year === year) return d;
    }
    // Fallback (DST gap edge case — should never happen for our cutoff hours)
    return new Date(`${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(min)}:00-07:00`);
  }

  /**
   * Return a UTC Date that falls at Pacific noon n calendar days after
   * the given Pacific date. Anchoring at noon avoids DST-boundary edge cases
   * where adding exactly 86 400 000 ms crosses into the wrong calendar day.
   */
  function ptDatePlusN(year, month, day, n) {
    const noon = ptToUTC(year, month, day, 12);
    return new Date(noon.getTime() + n * 86_400_000);
  }

  /** Format a UTC Date as a short Pacific-time date string, e.g. "Tue, Jun 17". */
  function fmtDate(utcDate) {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: TZ, weekday: 'short', month: 'short', day: 'numeric',
    }).format(utcDate);
  }

  /** Return the PHASES entry that contains the given moment. */
  function getPhase(now) {
    const { dow, h } = getPT(now);
    // Phase A: Fri 01:00 → Mon 01:00
    if ((dow === 5 && h >= 1) || dow === 6 || dow === 0 || (dow === 1 && h < 1)) return PHASES[0];
    // Phase B: Mon 01:00 → Tue 01:00
    if ((dow === 1 && h >= 1) || (dow === 2 && h < 1)) return PHASES[1];
    // Phase C: Tue 01:00 → Fri 01:00
    return PHASES[2];
  }

  /** Return the UTC Date of the next cutoff for the given phase. */
  function nextCutoff(now, phase) {
    const pt = getPT(now);
    let days = (phase.cutoffDow - pt.dow + 7) % 7;
    // Already at (or past) the cutoff hour on the target day → push to next week
    if (days === 0 && pt.h >= phase.cutoffHour) days = 7;

    // Walk forward from Pacific midnight of today by `days` full days
    const todayMidnight = ptToUTC(pt.year, pt.month, pt.day, 0);
    const targetMidnight = new Date(todayMidnight.getTime() + days * 86_400_000);
    const tp = getPT(targetMidnight);
    return ptToUTC(tp.year, tp.month, tp.day, phase.cutoffHour);
  }

  /** Compute all values needed to populate the UI. */
  function compute(now) {
    const phase = getPhase(now);
    const cutoff = nextCutoff(now, phase);
    const cp = getPT(cutoff);

    const ship  = ptDatePlusN(cp.year, cp.month, cp.day, phase.shipOffset);
    const sp    = getPT(ship);
    const socal = ptDatePlusN(sp.year, sp.month, sp.day, phase.socalOffset);
    const norcal = ptDatePlusN(sp.year, sp.month, sp.day, phase.norcalOffset);

    const msLeft    = cutoff - now;
    const totalMins = Math.max(0, Math.floor(msLeft / 60_000));

    return {
      hours:     Math.floor(totalMins / 60),
      minutes:   totalMins % 60,
      shipDate:  fmtDate(ship),
      socalDate: fmtDate(socal),
      norcalDate: fmtDate(norcal),
      expired:   msLeft <= 0,
    };
  }

  function render() {
    const nodes = document.querySelectorAll('[data-shipping-countdown]');
    if (!nodes.length) return;

    const info = compute(new Date());

    nodes.forEach(el => {
      const timer    = el.querySelector('[data-countdown-timer]');
      const delivery = el.querySelector('[data-delivery-estimate]');

      if (timer) {
        timer.textContent = info.expired
          ? `Order now to ship by ${info.shipDate}`
          : `Order within ${info.hours}h ${info.minutes}m to ship by ${info.shipDate}`;
      }
      if (delivery) {
        delivery.textContent =
          `Est. Delivery: ${info.socalDate} (SoCal) · ${info.norcalDate} (NorCal)`;
      }
    });
  }

  // Kick off immediately and refresh every minute
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { render(); setInterval(render, 60_000); });
  } else {
    render();
    setInterval(render, 60_000);
  }
})();
